import React, { useRef, useState } from 'react'
import {
  FaIcon,
  faCalendarLinesPenSolid,
  faChessClockSolid,
  faChevronRightSolid,
} from '@/lib/fa'
import { axiosInstance } from '@/lib/axios'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  getOvertimeOptionName,
  OverTimeOptions,
} from '@/modules/drivers/helpers/TimeSheetOverTimeNameResolver'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'

export const TIMESHEET_CONTEXT_MENU_ID = 'timesheet-context-menu'

type TimeSheetContextMenuProps = {
  isOpen: boolean
  position: { x: number; y: number }
  onClose: () => void
  selectedDates?: string[]
  onChangeDayType?: () => void
  onSettingsChanged?: () => void

  companyId: string
  driverId: string
}

const TimeSheetContextMenu: React.FC<TimeSheetContextMenuProps> = ({
  isOpen,
  position,
  onClose,
  selectedDates = [],
  onChangeDayType,
  onSettingsChanged,
  driverId,
  companyId,
}) => {
  const [isOvertimeSubmenuOpen, setIsOvertimeSubmenuOpen] = useState(false)
  const [submenuOpenLeft, setSubmenuOpenLeft] = useState(false)
  const overtimeMenuRef = useRef<HTMLDivElement>(null)
  const { t } = useLocalize()
  if (!isOpen) return null

  const changeOvetime = async ({ type }: { type: OverTimeOptions }) => {
    try {
      await axiosInstance.post(
        `/api/${companyId}/timesheet/postponeOverhours`,
        {
          driverId,
          dates: selectedDates,
          actionType: type,
        },
      )

      onSettingsChanged?.()
    } catch (err) {
      console.error('Error changing overtime:', err)
    } finally {
      onClose()
    }
  }

  const adjustedLeft =
    position.x + 240 > window.innerWidth ? position.x - 240 : position.x
  const adjustedTop =
    position.y + 120 > window.innerHeight ? position.y - 120 : position.y

  return (
    <>
      <div
        className="tw-fixed tw-inset-0 tw-z-[9998]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault()
        }}
      />
      <div
        className="tw-fixed tw-z-[9999] tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-lg"
        style={{ top: adjustedTop, left: adjustedLeft }}
      >
        <div
          onClick={() => {
            onChangeDayType?.()
            onClose()
          }}
          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
        >
          <FaIcon
            icon={faCalendarLinesPenSolid}
            className="tw-h-3.5 tw-w-3.5 tw-text-content-base"
          />
          {t('timesheet.change-day-type')}
        </div>

        <div
          className="tw-relative"
          ref={overtimeMenuRef}
          onMouseEnter={() => {
            if (overtimeMenuRef.current) {
              const rect = overtimeMenuRef.current.getBoundingClientRect()
              const submenuWidth = 250
              setSubmenuOpenLeft(rect.right + submenuWidth > window.innerWidth)
            }
            setIsOvertimeSubmenuOpen(true)
          }}
          onMouseLeave={() => setIsOvertimeSubmenuOpen(false)}
        >
          <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10">
            <FaIcon
              icon={faChessClockSolid}
              className="tw-h-3.5 tw-w-3.5 tw-text-content-base"
            />
            <span className="tw-flex-1">
              {t(getOvertimeOptionName(OverTimeOptions.AllFromActivePart))}
            </span>
            <FaIcon
              icon={faChevronRightSolid}
              className="tw-text-content-muted tw-ml-2 tw-h-3 tw-w-3"
            />
          </div>

          {isOvertimeSubmenuOpen && (
            <div
              className={`tw-absolute tw-top-0 tw-z-[10000] tw-min-w-[250px] tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-lg ${submenuOpenLeft ? 'tw-right-full' : 'tw-left-full'}`}
            >
              <div
                onClick={() => {
                  changeOvetime({ type: OverTimeOptions.SelectedDays })
                }}
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
              >
                {t(getOvertimeOptionName(OverTimeOptions.SelectedDays))}
              </div>
              <div
                onClick={() => {
                  changeOvetime({ type: OverTimeOptions.AboveLumpsum })
                }}
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
              >
                {t(getOvertimeOptionName(OverTimeOptions.AboveLumpsum))}
              </div>
              <div
                onClick={() => {
                  changeOvetime({ type: OverTimeOptions.AllFromActivePart })
                }}
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
              >
                {t(getOvertimeOptionName(OverTimeOptions.AllFromActivePart))}
              </div>
              <div className="tw-py-1">
                <DividerHorizontal />
              </div>
              <div
                onClick={() => {
                  changeOvetime({ type: OverTimeOptions.ClearSelectedDays })
                }}
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
              >
                {t(getOvertimeOptionName(OverTimeOptions.ClearSelectedDays))}
              </div>
              <div
                onClick={() => {
                  changeOvetime({ type: OverTimeOptions.ClearAll })
                }}
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-primary/10"
              >
                {t(getOvertimeOptionName(OverTimeOptions.ClearAll))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default TimeSheetContextMenu
